import React, { useEffect, useMemo, useState } from 'react';

const FEEDBACK_TYPES = [
    { value: 'BUG', label: 'Bug', styleOptions: { background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)'} },
    { value: 'IDEA', label: 'Idea', styleOptions: { background: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.3)'} },
    { value: 'QUESTION', label: 'Question', styleOptions: { background: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)'} },
    { value: 'PRAISE', label: 'Praise', styleOptions: { background: 'rgba(16,185,129,0.15)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)'} }
];

const defaultPlaceholders = {
  BUG: 'What happened? Steps to reproduce? Expected vs actual behavior?',
  IDEA: 'What idea would make the product better? Any example or workflow?',
  QUESTION: 'What are you trying to understand? Include context and goal.',
  PRAISE: 'What worked well? What should we keep doing?'
};

const FeedbackModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialType = 'BUG',
  initialRating = null
}) => {
  const [type, setType] = useState(initialType);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const placeholder = useMemo(() => defaultPlaceholders[type] || defaultPlaceholders.BUG, [type]);

  useEffect(() => {
    if (isOpen) {
      setType(initialType);
      setTitle('');
      setDescription('');
      setIsSubmitting(false);
      setIsSubmitted(false);
    }
  }, [isOpen, initialType]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!title.trim() || !description.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        type,
        title: title.trim(),
        description: description.trim(),
        rating: initialRating
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="rounded-lg shadow-xl p-6 w-[520px] max-w-[92vw]" style={{background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold" style={{color: 'rgba(255,255,255,0.85)'}}>Share feedback</h3>
            <p className="text-sm" style={{color: 'rgba(255,255,255,0.5)'}}>Help us improve with a quick report.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="transition-colors"
            style={{color: 'rgba(255,255,255,0.4)'}}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.85)'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
            aria-label="Close feedback modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isSubmitted ? (
          <div className="mt-5 rounded-lg p-4" style={{background: 'rgba(130,255,207,0.1)', border: '1px solid rgba(130,255,207,0.3)'}}>
            <p className="text-sm font-semibold" style={{color: 'var(--tqa-green)'}}>Feedback submitted</p>
            <p className="mt-1 text-sm" style={{color: 'rgba(130,255,207,0.8)'}}>
              Thank you for your report. Your feedback has been sent to the dev team.
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="btn-primary px-4 py-2 text-sm font-medium rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <div>
              <p className="text-xs font-semibold mb-2" style={{color: 'rgba(255,255,255,0.5)'}}>Category</p>
              <div className="flex flex-wrap gap-2">
                {FEEDBACK_TYPES.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => setType(item.value)}
                      className="px-3 py-1.5 text-xs font-semibold rounded-full transition-colors"
                      style={
                        type === item.value 
                        ? item.styleOptions 
                        : {background:'var(--tqa-surface-3)',color:'rgba(255,255,255,0.55)',border:'1px solid var(--tqa-border-dark)'}
                      }
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold" style={{color: 'rgba(255,255,255,0.5)'}}>Title</label>
              <input
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Short summary"
                className="mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
                maxLength={200}
                required
              />
            </div>

            <div>
              <label className="text-xs font-semibold" style={{color: 'rgba(255,255,255,0.5)'}}>Details</label>
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder={placeholder}
                className="mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none min-h-[120px]"
                style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
                maxLength={5000}
                required
              />
            </div>

            {initialRating !== null && initialRating !== undefined && (
              <div className="text-xs" style={{color: 'rgba(255,255,255,0.4)'}}>
                Rating captured: {initialRating > 0 ? 'Thumbs up' : 'Thumbs down'}
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-xs" style={{color: 'rgba(255,255,255,0.4)'}}>
                Your feedback is sent directly to the dev team.
              </p>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
